import { emptyFrame } from './Empty';

/**
 * Updates frames of an image based on provided updates and state configuration.
 *
 * @param {Object} image - The source image object containing frames.
 * @param {Object} updates - The updates to apply { delay, x, y }.
 * @param {Object} config - Configuration objects { selectedIndices, emptiedIndices, deletedIndices, frameOrder }.
 * @returns {Promise<Object>} - A promise resolving to the new image object with updated frames.
 */
export const updateImageFrames = async (image, updates, config) => {
  const { delay, x, y } = updates;
  const {
    selectedIndices,
    emptiedIndices,
    deletedIndices,
    frameOrder,
  } = config;

  // Determine the final frame order
  let finalOrder = frameOrder;
  if (!finalOrder || finalOrder.length === 0) {
    finalOrder = image.frames.map((_, index) => index);
  }

  // Filter out deleted frames
  const effectiveOrder = finalOrder.filter((idx) => !deletedIndices.includes(idx));

  // Create promises for each frame update
  const updatePromises = image.frames.map((frame, index) => {
    const shouldUpdate = selectedIndices.includes(index);
    const shouldEmpty = emptiedIndices.includes(index);

    const applyUpdates = (f) => {
      if (shouldUpdate) {
        // Ensure we create a new object
        return {
          ...f,
          delay: delay !== undefined ? delay : f.delay,
          x: x !== undefined ? x : f.x,
          y: y !== undefined ? y : f.y,
        };
      }
      return f;
    };

    if (shouldEmpty) {
      return emptyFrame(frame).then(applyUpdates);
    } else {
      return Promise.resolve(applyUpdates(frame));
    }
  });

  const newFrames = await Promise.all(updatePromises);

  // Reorder based on effectiveOrder
  const reorderedFrames = effectiveOrder.map((index) => newFrames[index]);

  return { ...image, frames: reorderedFrames };
};
